"use client"

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react"
import type { PromptSnippet } from "@/schema/prompt-snippet"
import { cn } from "@ui/utils"
import { createPortal } from "react-dom"

import { PromptSnippetPill } from "@/components/chat-threads/prompt-snippet-pill"

function matchSlashCommand(value: string, cursor: number) {
  const beforeCursor = value.slice(0, cursor)
  const match = beforeCursor.match(/(^|[\s\n])\/([^\s]*)$/)
  if (!match) return null

  const query = match[2]
  return { start: beforeCursor.length - query.length - 1, query }
}

function snippetMatchesQuery(snippet: PromptSnippet, query: string) {
  const lower = query.toLowerCase()
  if (!lower) return true

  return snippet.title.toLowerCase().includes(lower) || snippet.content.toLowerCase().includes(lower)
}

function snippetOptionId(snippet: PromptSnippet) {
  return `prompt-snippet-option-${snippet.id ?? snippet.key}`
}

const SLASH_MENU_LISTBOX_ID = "prompt-snippet-slash-menu"

function PromptSnippetSlashMenuItem({
  isSelected,
  onClick,
  onMouseEnter,
  snippet,
}: {
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  snippet: PromptSnippet
}) {
  const itemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (!isSelected || !itemRef.current) return

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    itemRef.current.scrollIntoView({ block: "nearest", behavior: reducedMotion ? "auto" : "smooth" })
  }, [isSelected])

  return (
    <li
      ref={itemRef}
      id={snippetOptionId(snippet)}
      tabIndex={-1}
      className={cn("flex cursor-pointer px-0.5 py-1", isSelected && "bg-secondary")}
      role="option"
      aria-selected={isSelected}
      onMouseEnter={onMouseEnter}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      <PromptSnippetPill
        title={snippet.title}
        icon={snippet.icon}
        color={snippet.color}
        className="w-fit max-w-full border-0 bg-transparent"
      />
    </li>
  )
}

export function PromptSnippetSlashMenu({
  snippets,
  textareaRef,
  disabled,
  onSelect,
}: {
  snippets: PromptSnippet[]
  textareaRef: RefObject<HTMLTextAreaElement | null>
  disabled?: boolean
  onSelect: (snippet: PromptSnippet) => void
}) {
  const [match, setMatch] = useState<{ start: number; query: string } | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)
  const [menuPortal, setMenuPortal] = useState<HTMLElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const options = useMemo(() => {
    if (!match) return []
    return snippets.filter((snippet) => snippetMatchesQuery(snippet, match.query))
  }, [match, snippets])

  const matchKey = match ? `${match.start}:${match.query}` : null
  const isOpen = !disabled && snippets.length > 0 && match !== null && dismissedKey !== matchKey

  useEffect(() => {
    if (match === null && dismissedKey !== null) setDismissedKey(null)
  }, [dismissedKey, match])

  const optionsRef = useRef(options)
  const highlightedIndexRef = useRef(highlightedIndex)
  const matchKeyRef = useRef(matchKey)
  const isOpenRef = useRef(isOpen)
  const disabledRef = useRef(disabled)
  const onSelectRef = useRef(onSelect)

  optionsRef.current = options
  highlightedIndexRef.current = highlightedIndex
  matchKeyRef.current = matchKey
  isOpenRef.current = isOpen
  disabledRef.current = disabled
  onSelectRef.current = onSelect

  const syncMatch = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      setMatch(null)
      return
    }

    setMatch(matchSlashCommand(textarea.value, textarea.selectionStart ?? 0))
  }, [textareaRef])

  useLayoutEffect(() => {
    setHighlightedIndex(0)
  }, [matchKey])

  const clearComposer = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.value = ""
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    textarea.dispatchEvent(new Event("change", { bubbles: true }))
  }, [textareaRef])

  const selectSnippet = useCallback(
    (snippet: PromptSnippet) => {
      if (disabledRef.current) return

      setDismissedKey(null)
      setMatch(null)
      clearComposer()
      onSelectRef.current(snippet)
    },
    [clearComposer]
  )

  const positionMenu = useCallback(() => {
    const portalEl = menuRef.current
    const textarea = textareaRef.current
    if (!portalEl || !textarea) return

    const rect = textarea.getBoundingClientRect()
    const menuHeight = portalEl.offsetHeight
    const top = Math.max(8, Math.round(rect.top - menuHeight - 8))
    const left = Math.round(rect.left)

    portalEl.style.transform = `translate(${left}px, ${top}px)`
  }, [textareaRef])

  useEffect(() => {
    const menuElement = document.createElement("div")
    menuElement.className = "prompt-snippet-slash-menu-portal"
    Object.assign(menuElement.style, {
      position: "fixed",
      left: "0px",
      top: "0px",
      zIndex: "9999",
    })
    document.body.appendChild(menuElement)
    setMenuPortal(menuElement)

    return () => {
      if (document.body.contains(menuElement)) {
        document.body.removeChild(menuElement)
      }
    }
  }, [])

  useEffect(() => {
    const isTextareaEvent = (event: Event) => event.target === textareaRef.current

    const handleSync = (event: Event) => {
      if (!isTextareaEvent(event)) return
      syncMatch()
    }

    const handleBlur = (event: FocusEvent) => {
      if (!isTextareaEvent(event)) return
      window.setTimeout(() => {
        if (document.activeElement === textareaRef.current) return
        setMatch(null)
      }, 0)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTextareaEvent(event) || !isOpenRef.current) return

      if (event.key === "ArrowDown") {
        event.preventDefault()
        setHighlightedIndex((current) => Math.min(current + 1, Math.max(optionsRef.current.length - 1, 0)))
        return
      }

      if (event.key === "ArrowUp") {
        event.preventDefault()
        setHighlightedIndex((current) => Math.max(current - 1, 0))
        return
      }

      if (event.key === "Escape") {
        event.preventDefault()
        setDismissedKey(matchKeyRef.current)
        return
      }

      if (event.key !== "Enter" || event.shiftKey || event.isComposing || event.keyCode === 229) return

      const snippet = optionsRef.current[highlightedIndexRef.current]
      if (!snippet) return

      event.preventDefault()
      event.stopPropagation()
      selectSnippet(snippet)
    }

    document.addEventListener("input", handleSync, true)
    document.addEventListener("click", handleSync, true)
    document.addEventListener("keyup", handleSync, true)
    document.addEventListener("blur", handleBlur, true)
    document.addEventListener("keydown", handleKeyDown, true)

    syncMatch()

    return () => {
      document.removeEventListener("input", handleSync, true)
      document.removeEventListener("click", handleSync, true)
      document.removeEventListener("keyup", handleSync, true)
      document.removeEventListener("blur", handleBlur, true)
      document.removeEventListener("keydown", handleKeyDown, true)
    }
  }, [selectSnippet, syncMatch, textareaRef])

  useEffect(() => {
    if (!isOpen) return

    const handleReposition = () => positionMenu()
    window.addEventListener("scroll", handleReposition, true)
    window.addEventListener("resize", handleReposition)
    return () => {
      window.removeEventListener("scroll", handleReposition, true)
      window.removeEventListener("resize", handleReposition)
    }
  }, [isOpen, positionMenu])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.setAttribute("aria-expanded", isOpen ? "true" : "false")
    textarea.setAttribute("aria-haspopup", "listbox")
    textarea.setAttribute("role", "combobox")
    textarea.setAttribute("aria-autocomplete", "list")

    if (isOpen) {
      textarea.setAttribute("aria-controls", SLASH_MENU_LISTBOX_ID)
      const activeSnippet = options[highlightedIndex]
      if (activeSnippet) {
        textarea.setAttribute("aria-activedescendant", snippetOptionId(activeSnippet))
      } else {
        textarea.removeAttribute("aria-activedescendant")
      }
    } else {
      textarea.removeAttribute("aria-controls")
      textarea.removeAttribute("aria-activedescendant")
    }

    return () => {
      textarea.removeAttribute("aria-expanded")
      textarea.removeAttribute("aria-haspopup")
      textarea.removeAttribute("role")
      textarea.removeAttribute("aria-autocomplete")
      textarea.removeAttribute("aria-controls")
      textarea.removeAttribute("aria-activedescendant")
    }
  }, [highlightedIndex, isOpen, options, textareaRef])

  if (!isOpen || !menuPortal) return null

  return createPortal(
    <div
      ref={(node) => {
        menuRef.current = node
        if (node) requestAnimationFrame(() => requestAnimationFrame(positionMenu))
      }}
      id={SLASH_MENU_LISTBOX_ID}
      className="bg-background pointer-events-auto w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border shadow-md"
      role="listbox"
      aria-label="Prompt snippets"
      onMouseDown={(event) => event.preventDefault()}
    >
      {options.length === 0 ? (
        <div className="text-muted-foreground px-3 py-2 text-sm">No matching snippets</div>
      ) : (
        <ul className="m-0 max-h-72 list-none overflow-y-auto p-0">
          {options.map((snippet, index) => (
            <PromptSnippetSlashMenuItem
              key={snippet.id ?? snippet.key}
              snippet={snippet}
              isSelected={highlightedIndex === index}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectSnippet(snippet)}
            />
          ))}
        </ul>
      )}
    </div>,
    menuPortal
  )
}
